// Uso del ERP (solo dirección): qué pantallas usa cada persona medida, en qué
// orden, qué acciones repite, cuánto tarda y qué señales de problema deja
// (errores, clics repetidos, recargas, abandonos rápidos). Datos de
// GET /api/uso/resumen (uso_erp_routes.py); el registro lo hace utils/usoErp.js.
// Arriba, la lectura de la noche (GET /api/uso/analisis, revisión con IA) y las
// sugerencias de cambio (GET/PATCH /api/uso/sugerencias), independientes del periodo.
// Acepta ?usuario_id=<id> para abrir directamente el detalle de una persona.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity, AlertTriangle, Copy, RefreshCw } from 'lucide-react';
import { usoErpAPI } from '../api/api';
import { hoyMadrid } from '../utils/fechas';
import { AvisoInline, DialogoConfirmar, useAvisoTemporal } from '../components/empleados/RegistroHorarioEmpleado';

const RANGOS = [
  { id: '7', label: '7 días' },
  { id: '30', label: '30 días' },
  { id: '90', label: '90 días' },
  { id: 'personalizado', label: 'Personalizado' },
];

const restarDias = (fechaIso, dias) => {
  const [a, m, d] = fechaIso.split('-').map(Number);
  const f = new Date(Date.UTC(a, m - 1, d));
  f.setUTCDate(f.getUTCDate() - dias);
  return f.toISOString().slice(0, 10);
};

const fmtFechaHora = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
};

const fmtHora = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch (e) {
    return iso;
  }
};

const fmtMinutos = (min) => {
  const n = Number(min) || 0;
  if (n < 60) return `${n.toLocaleString('es-ES', { maximumFractionDigits: 1 })} min`;
  const h = Math.floor(n / 60);
  return `${h} h ${Math.round(n - h * 60)} min`;
};

const fmtFechaCorta = (fecha) => {
  if (!fecha) return '';
  const [, m, d] = fecha.split('-');
  return `${d}/${m}`;
};

const TIPOS_EVENTO = {
  pagina: { texto: 'Pantalla', clase: 'bg-blue-100 text-blue-800' },
  accion: { texto: 'Acción', clase: 'bg-emerald-100 text-emerald-800' },
  sesion: { texto: 'Pestaña', clase: 'bg-gray-100 text-gray-700' },
  error: { texto: 'Error', clase: 'bg-red-100 text-red-700' },
  clic_repetido: { texto: 'Clics', clase: 'bg-amber-100 text-amber-800' },
  recarga: { texto: 'Recarga', clase: 'bg-purple-100 text-purple-800' },
};

const Tarjeta = ({ titulo, children, extra, testid }) => (
  <section className="bg-white rounded-xl border border-gray-200 p-4" data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <h2 className="text-sm font-semibold text-gray-800">{titulo}</h2>
      {extra}
    </div>
    {children}
  </section>
);

const Vacio = ({ texto = 'Sin datos en el periodo' }) => (
  <p className="text-sm text-gray-400">{texto}</p>
);

const Barra = ({ etiqueta, valor, max, texto, color = 'bg-duetto-primary' }) => {
  const pct = max > 0 ? Math.max(2, Math.round((valor / max) * 100)) : 0;
  return (
    <div className="py-1">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-gray-700 truncate min-w-0" title={etiqueta}>{etiqueta}</span>
        <span className="text-gray-500 shrink-0">{texto}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full mt-1 overflow-hidden">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const Indicador = ({ titulo, valor, detalle }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-3">
    <p className="text-xs text-gray-500">{titulo}</p>
    <p className="text-xl font-bold text-gray-900 mt-0.5">{valor}</p>
    {detalle && <p className="text-xs text-gray-400 mt-0.5">{detalle}</p>}
  </div>
);

// ─── Señales de problema ────────────────────────────────────────────────

const ListaSenal = ({ titulo, filas, render }) => (
  <div>
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{titulo}</h3>
    {filas && filas.length > 0 ? (
      <ul className="space-y-1.5">
        {filas.map((f, i) => (
          <li key={`${titulo}-${f.nombre_evento || f.ruta}-${i}`} className="text-sm text-gray-700 border-l-2 border-red-200 pl-2">
            {render(f)}
          </li>
        ))}
      </ul>
    ) : (
      <Vacio texto="Nada en el periodo" />
    )}
  </div>
);

const SenalesProblema = ({ senales }) => {
  if (!senales) return null;
  const recientes = senales.errores_ultimos_7_dias || 0;
  return (
    <Tarjeta
      titulo="Señales de problema"
      testid="uso-senales"
      extra={recientes > 0 ? (
        <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full" data-testid="uso-errores-7-dias">
          {recientes} {recientes === 1 ? 'error' : 'errores'} en 7 días
        </span>
      ) : (
        <span className="text-xs text-gray-400">Sin errores en 7 días</span>
      )}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <ListaSenal
          titulo="Errores del API"
          filas={senales.errores_api}
          render={(f) => (
            <>
              <p>{f.frase}</p>
              <p className="text-xs text-gray-400">Último: {f.ultimo_detalle || '-'} · {fmtFechaHora(f.ultimo)}</p>
            </>
          )}
        />
        <ListaSenal
          titulo="Errores de pantalla"
          filas={senales.errores_js}
          render={(f) => (
            <>
              <p className="break-words">{f.frase}</p>
              {f.ultimo_detalle && <p className="text-xs text-gray-400">{f.ultimo_detalle}</p>}
            </>
          )}
        />
        <ListaSenal titulo="Clics repetidos" filas={senales.clics_repetidos} render={(f) => <p>{f.frase}</p>} />
        <ListaSenal
          titulo={`Recargas (${senales.recargas?.total || 0}, ${senales.recargas?.seguidas || 0} seguidas)`}
          filas={senales.recargas?.por_ruta}
          render={(f) => <p>{f.frase}</p>}
        />
        <ListaSenal titulo="Abandonos rápidos" filas={senales.abandonos} render={(f) => <p>{f.frase}</p>} />
      </div>
    </Tarjeta>
  );
};

// ─── Detalle de una persona ─────────────────────────────────────────────

const Candidatos = ({ candidatos }) => {
  const [copiado, setCopiado] = useState(null);

  const copiar = async (frase, indice) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(frase);
      } else {
        const area = document.createElement('textarea');
        area.value = frase;
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      setCopiado({ indice, ok: true });
    } catch (e) {
      setCopiado({ indice, ok: false });
    }
    setTimeout(() => setCopiado(null), 2500);
  };

  if (!candidatos || candidatos.length === 0) return <Vacio texto="Ninguna secuencia se repite lo bastante todavía" />;
  return (
    <ul className="space-y-2">
      {candidatos.map((c, i) => (
        <li key={`${c.frase}-${i}`} className="flex flex-col sm:flex-row sm:items-start gap-2 justify-between border border-gray-100 rounded-lg p-2.5">
          <div className="min-w-0">
            <p className="text-sm text-gray-800">{c.frase}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {c.tipo === 'secuencia' ? `${c.pasos.length} pasos` : 'Repetición en un día'} · {c.veces} veces
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {copiado && copiado.indice === i && (
              <span className={`text-xs ${copiado.ok ? 'text-emerald-700' : 'text-red-600'}`}>
                {copiado.ok ? 'Copiado al portapapeles' : 'No se pudo copiar'}
              </span>
            )}
            <button
              type="button"
              onClick={() => copiar(c.frase, i)}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1"
              data-uso="uso_erp.anotar"
            >
              <Copy className="w-3.5 h-3.5" /> Anotar
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
};

const DetallePersona = ({ detalle }) => {
  const paginas = detalle.paginas || [];
  const acciones = detalle.acciones || [];
  const maxVisitas = Math.max(0, ...paginas.map((p) => p.visitas));
  const maxAcciones = Math.max(0, ...acciones.map((a) => a.veces));
  const horas = detalle.horas || [];
  const maxHora = Math.max(0, ...horas);
  const dias = detalle.dias || [];
  const maxDia = Math.max(0, ...dias.map((d) => d.eventos));
  const disp = detalle.dispositivos || {};
  const totalDisp = (disp.movil || 0) + (disp.escritorio || 0);
  const sesionesTotal = detalle.sesiones_total || 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Indicador titulo="Tiempo estimado" valor={fmtMinutos(detalle.minutos_estimados)} />
        <Indicador titulo="Sesiones" valor={sesionesTotal} />
        <Indicador
          titulo="Pantallas por sesión"
          valor={sesionesTotal ? (detalle.paginas_total / sesionesTotal).toLocaleString('es-ES', { maximumFractionDigits: 1 }) : '0'}
        />
        <Indicador titulo="Desde el móvil" valor={`${totalDisp ? Math.round((100 * (disp.movil || 0)) / totalDisp) : 0} %`} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Tarjeta titulo="Pantallas más usadas">
          {paginas.length === 0 ? <Vacio /> : paginas.map((p) => (
            <Barra
              key={p.ruta}
              etiqueta={p.ruta}
              valor={p.visitas}
              max={maxVisitas}
              texto={`${p.visitas} visitas · ${fmtMinutos(p.minutos)}${p.media_segundos ? ` · ${p.media_segundos} s de media` : ''}`}
            />
          ))}
        </Tarjeta>
        <Tarjeta titulo="Acciones más repetidas">
          {acciones.length === 0 ? <Vacio /> : acciones.map((a) => (
            <Barra
              key={`${a.nombre_evento}-${a.ruta}`}
              etiqueta={`${a.nombre_evento} (${a.ruta})`}
              valor={a.veces}
              max={maxAcciones}
              texto={`${a.veces} veces`}
              color="bg-emerald-500"
            />
          ))}
        </Tarjeta>
      </div>

      <Tarjeta titulo="Candidatos a automatizar">
        <Candidatos candidatos={detalle.candidatos_automatizacion} />
      </Tarjeta>

      <Tarjeta titulo="Recorridos habituales">
        {(detalle.recorridos || []).length === 0 ? <Vacio /> : (
          <ul className="space-y-1.5">
            {detalle.recorridos.map((r) => (
              <li key={r.pasos.join('>')} className="text-sm text-gray-700 flex items-start justify-between gap-3">
                <span className="min-w-0 break-words">{r.pasos.join(', luego ')}</span>
                <span className="text-xs text-gray-500 shrink-0">{r.veces} veces</span>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>

      <div className="grid gap-4 lg:grid-cols-2">
        <Tarjeta titulo="Horas del día">
          <div className="flex items-end gap-0.5 h-28">
            {horas.map((n, h) => (
              <div key={`h${h}`} className="flex-1 flex flex-col items-center justify-end h-full" title={`${h}:00 · ${n} eventos`}>
                <div className="w-full bg-duetto-primary/80 rounded-t" style={{ height: `${maxHora ? Math.round((n / maxHora) * 100) : 0}%` }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
            <span>0 h</span><span>6 h</span><span>12 h</span><span>18 h</span><span>23 h</span>
          </div>
        </Tarjeta>
        <Tarjeta titulo="Días">
          {dias.length === 0 ? <Vacio /> : (
            <div className="overflow-x-auto">
              <div className="flex items-end gap-1 h-28" style={{ minWidth: `${dias.length * 14}px` }}>
                {dias.map((d) => (
                  <div key={d.fecha} className="flex-1 flex flex-col items-center justify-end h-full min-w-[10px]" title={`${fmtFechaCorta(d.fecha)} · ${d.eventos} eventos`}>
                    <div className="w-full bg-sky-500 rounded-t" style={{ height: `${maxDia ? Math.round((d.eventos / maxDia) * 100) : 0}%` }} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>{fmtFechaCorta(dias[0].fecha)}</span>
                <span>{fmtFechaCorta(dias[dias.length - 1].fecha)}</span>
              </div>
            </div>
          )}
        </Tarjeta>
      </div>

      <Tarjeta titulo="Últimas sesiones">
        {(detalle.sesiones_recientes || []).length === 0 ? <Vacio /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-1.5 pr-2 font-medium">Inicio</th>
                  <th className="py-1.5 pr-2 font-medium">Fin</th>
                  <th className="py-1.5 pr-2 font-medium">Pantallas</th>
                  <th className="py-1.5 pr-2 font-medium">Dispositivo</th>
                  <th className="py-1.5 pr-2 font-medium">Primera</th>
                  <th className="py-1.5 font-medium">Última</th>
                </tr>
              </thead>
              <tbody>
                {detalle.sesiones_recientes.map((s) => (
                  <tr key={s.sesion_id} className="border-b border-gray-50">
                    <td className="py-1.5 pr-2 whitespace-nowrap">{fmtFechaHora(s.inicio)}</td>
                    <td className="py-1.5 pr-2 whitespace-nowrap">{fmtHora(s.fin)}</td>
                    <td className="py-1.5 pr-2">{s.paginas}</td>
                    <td className="py-1.5 pr-2">{s.dispositivo === 'movil' ? 'Móvil' : 'Escritorio'}</td>
                    <td className="py-1.5 pr-2 text-gray-600 max-w-[180px] truncate" title={s.primera_ruta}>{s.primera_ruta || '-'}</td>
                    <td className="py-1.5 text-gray-600 max-w-[180px] truncate" title={s.ultima_ruta}>{s.ultima_ruta || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>

      <Tarjeta titulo="Línea de tiempo (últimos 100 eventos)">
        {(detalle.ultimos_eventos || []).length === 0 ? <Vacio /> : (
          <ol className="space-y-1 max-h-[480px] overflow-y-auto pr-1">
            {detalle.ultimos_eventos.map((e, i) => {
              const tipo = TIPOS_EVENTO[e.tipo] || { texto: e.tipo, clase: 'bg-gray-100 text-gray-700' };
              const texto = e.tipo === 'pagina' ? e.ruta : (e.nombre_evento || e.ruta);
              return (
                <li key={`${e.t}-${i}`} className="flex items-start gap-2 text-xs">
                  <span className="text-gray-400 shrink-0 w-[92px]">{fmtFechaHora(e.t)}</span>
                  <span className={`px-1.5 py-0.5 rounded shrink-0 ${tipo.clase}`}>{tipo.texto}</span>
                  <span className="text-gray-700 min-w-0 break-words">
                    {texto}
                    {e.tipo !== 'pagina' && e.ruta && e.nombre_evento && <span className="text-gray-400"> · {e.ruta}</span>}
                    {e.detalle && <span className="text-gray-400"> · {e.detalle}</span>}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </Tarjeta>
    </div>
  );
};

// ─── Lectura de la noche (revisión con IA) ──────────────────────────────

const fmtFechaLarga = (fecha) => {
  if (!fecha) return '';
  const [a, m, d] = String(fecha).slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};

const GRAVEDAD = {
  alta: { texto: 'Alta', borde: 'border-red-300 bg-red-50', etiqueta: 'bg-red-600 text-white' },
  media: { texto: 'Media', borde: 'border-amber-200 bg-amber-50', etiqueta: 'bg-amber-500 text-white' },
  baja: { texto: 'Baja', borde: 'border-gray-200 bg-gray-50', etiqueta: 'bg-gray-400 text-white' },
};

const NIVEL_CLASE = {
  alto: 'bg-emerald-100 text-emerald-800',
  medio: 'bg-sky-100 text-sky-800',
  bajo: 'bg-gray-100 text-gray-700',
};

const BloqueLectura = ({ titulo, vacio, children, hay }) => (
  <div>
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{titulo}</h3>
    {hay ? children : <Vacio texto={vacio} />}
  </div>
);

const LecturaNoche = () => {
  const [lecturas, setLecturas] = useState([]);
  const [indice, setIndice] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const res = await usoErpAPI.analisis(7);
        if (vivo) setLecturas(res.data?.analisis || []);
      } catch (e) {
        const detalle = e?.response?.data?.detail;
        if (vivo) setError(typeof detalle === 'string' ? detalle : 'No se pudo cargar la lectura de la noche');
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  const lectura = lecturas[indice];
  const anomalias = lectura?.anomalias || [];
  const patrones = lectura?.patrones || [];
  const automatizaciones = lectura?.automatizaciones || [];
  const errores = lectura?.errores_destacados || [];

  return (
    <Tarjeta
      titulo={lectura ? `Lectura de la noche · ${fmtFechaLarga(lectura.fecha_dia)}` : 'Lectura de la noche'}
      testid="uso-lectura-noche"
      extra={lecturas.length > 1 ? (
        <select
          value={indice}
          onChange={(e) => setIndice(Number(e.target.value))}
          className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
          data-testid="uso-lectura-selector"
          data-uso="uso_erp.lectura_anterior"
        >
          {lecturas.map((l, i) => (
            <option key={l.id || l.fecha_dia} value={i}>
              {i === 0 ? `Última (${fmtFechaLarga(l.fecha_dia)})` : fmtFechaLarga(l.fecha_dia)}
            </option>
          ))}
        </select>
      ) : null}
    >
      {cargando && <p className="text-sm text-gray-500">Cargando...</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!cargando && !error && !lectura && (
        <Vacio texto="Todavía no hay ninguna lectura de la noche. La IA revisa el uso de cada día de madrugada." />
      )}
      {lectura && (
        <div className="space-y-4">
          <p className="text-sm text-gray-800">{lectura.resumen || 'Sin novedades'}</p>
          <div className="grid gap-4 lg:grid-cols-2">
            <BloqueLectura titulo={`Anomalías (${anomalias.length})`} vacio="Ninguna" hay={anomalias.length > 0}>
              <ul className="space-y-2">
                {anomalias.map((a, i) => {
                  const g = GRAVEDAD[a.gravedad] || GRAVEDAD.media;
                  return (
                    <li key={`an-${a.titulo}-${i}`} className={`border rounded-lg p-2.5 ${g.borde}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900 min-w-0">{a.titulo}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold shrink-0 ${g.etiqueta}`}>{g.texto}</span>
                      </div>
                      {a.persona && <p className="text-xs text-gray-500">{a.persona}</p>}
                      {a.texto && <p className="text-sm text-gray-700 mt-1">{a.texto}</p>}
                      {a.evidencia && <p className="text-xs text-gray-500 mt-1 break-words">{a.evidencia}</p>}
                    </li>
                  );
                })}
              </ul>
            </BloqueLectura>
            <BloqueLectura titulo={`Patrones (${patrones.length})`} vacio="Ninguno" hay={patrones.length > 0}>
              <ul className="space-y-2">
                {patrones.map((p, i) => (
                  <li key={`pa-${p.titulo}-${i}`} className="border border-gray-100 rounded-lg p-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900 min-w-0">{p.titulo}</p>
                      {p.veces > 0 && <span className="text-xs text-gray-500 shrink-0">{p.veces} veces</span>}
                    </div>
                    {p.persona && <p className="text-xs text-gray-500">{p.persona}</p>}
                    {p.texto && <p className="text-sm text-gray-700 mt-1">{p.texto}</p>}
                  </li>
                ))}
              </ul>
            </BloqueLectura>
          </div>
          <BloqueLectura titulo={`Automatizaciones (${automatizaciones.length})`} vacio="Ninguna" hay={automatizaciones.length > 0}>
            <ul className="space-y-2">
              {automatizaciones.map((a, i) => (
                <li key={`au-${a.titulo}-${i}`} className="border border-emerald-100 bg-emerald-50/40 rounded-lg p-2.5">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1">
                    <p className="text-sm font-medium text-gray-900 min-w-0">{a.titulo}</p>
                    {a.ahorro_estimado && <span className="text-xs text-emerald-800 shrink-0">Ahorro: {a.ahorro_estimado}</span>}
                  </div>
                  {a.texto && <p className="text-sm text-gray-700 mt-1">{a.texto}</p>}
                  {(a.pasos_actuales || []).length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">Hoy: {a.pasos_actuales.join(', luego ')}</p>
                  )}
                </li>
              ))}
            </ul>
          </BloqueLectura>
          {errores.length > 0 && (
            <BloqueLectura titulo={`Errores destacados (${errores.length})`} vacio="Ninguno" hay>
              <ul className="space-y-1.5">
                {errores.map((e, i) => (
                  <li key={`er-${e.nombre_evento}-${i}`} className="text-sm text-gray-700 border-l-2 border-red-200 pl-2">
                    <span className="font-medium break-words">{e.nombre_evento}</span>
                    {e.veces > 0 && <span className="text-xs text-gray-500"> · {e.veces} veces</span>}
                    {e.texto && <p className="text-sm text-gray-600">{e.texto}</p>}
                  </li>
                ))}
              </ul>
            </BloqueLectura>
          )}
        </div>
      )}
    </Tarjeta>
  );
};

// ─── Sugerencias de cambio en el ERP ────────────────────────────────────

const PESTANAS_SUGERENCIAS = [
  { id: 'nueva', label: 'Nuevas' },
  { id: 'aceptada', label: 'Aceptadas' },
  { id: 'hecha', label: 'Hechas' },
  { id: 'descartada', label: 'Descartadas' },
];

const AMBITOS_SUGERENCIAS = [
  { id: 'todas', label: 'Todas' },
  { id: 'erp', label: 'ERP' },
  { id: 'web', label: 'Web' },
];

const INSIGNIA_AMBITO = {
  erp: { texto: 'ERP', clase: 'bg-slate-100 text-slate-700' },
  web: { texto: 'Web', clase: 'bg-sky-100 text-sky-800' },
};

const ACCIONES_SUGERENCIA = {
  aceptada: { boton: 'Aceptar', titulo: 'Aceptar la sugerencia', confirmar: 'Aceptar', aviso: 'Sugerencia aceptada' },
  hecha: { boton: 'Hecha', titulo: 'Marcar la sugerencia como hecha', confirmar: 'Marcar como hecha', aviso: 'Sugerencia marcada como hecha' },
  descartada: { boton: 'Descartar', titulo: 'Descartar la sugerencia', confirmar: 'Descartar', aviso: 'Sugerencia descartada' },
};

const SugerenciasCambio = () => {
  const [searchParams] = useSearchParams();
  const ambitoInicial = ['erp', 'web'].includes(searchParams.get('ambito')) ? searchParams.get('ambito') : 'todas';
  const [datos, setDatos] = useState(null);
  const [pestana, setPestana] = useState('nueva');
  const [ambito, setAmbito] = useState(ambitoInicial);
  const [error, setError] = useState('');
  const [dialogo, setDialogo] = useState(null); // { sugerencia, estado }
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [errorDialogo, setErrorDialogo] = useState('');
  const { aviso, mostrarAviso, limpiarAviso } = useAvisoTemporal();

  const cargar = useCallback(async () => {
    setError('');
    try {
      const res = await usoErpAPI.sugerencias({ ambito: ambito === 'todas' ? undefined : ambito });
      setDatos(res.data);
    } catch (e) {
      const detalle = e?.response?.data?.detail;
      setError(typeof detalle === 'string' ? detalle : 'No se pudieron cargar las sugerencias');
    }
  }, [ambito]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrir = (sugerencia, estado) => {
    setDialogo({ sugerencia, estado });
    setNota(sugerencia.nota_direccion || '');
    setErrorDialogo('');
  };

  const cerrar = () => {
    if (guardando) return;
    setDialogo(null);
  };

  const confirmar = async () => {
    if (!dialogo) return;
    setGuardando(true);
    setErrorDialogo('');
    try {
      await usoErpAPI.actualizarSugerencia(dialogo.sugerencia.id, { estado: dialogo.estado, nota_direccion: nota.trim() });
      mostrarAviso('ok', ACCIONES_SUGERENCIA[dialogo.estado].aviso);
      setDialogo(null);
      await cargar();
    } catch (e) {
      const detalle = e?.response?.data?.detail;
      setErrorDialogo(typeof detalle === 'string' ? detalle : 'No se pudo guardar el cambio');
    } finally {
      setGuardando(false);
    }
  };

  const todas = datos?.sugerencias || [];
  const visibles = todas.filter((s) => s.estado === pestana);
  const conteo = datos?.conteo || {};
  const conteoAmbito = datos?.conteo_ambito || {};
  const accion = dialogo ? ACCIONES_SUGERENCIA[dialogo.estado] : null;
  const umbral = datos?.umbral_dias || 5;
  const avisosUmbral = [];
  if (datos && ambito !== 'web' && !datos.habilitadas) {
    avisosUmbral.push(`Las sugerencias del ERP empiezan cuando haya ${umbral} días de datos (llevamos ${datos.dias_con_datos || 0}).`);
  }
  if (datos && ambito !== 'erp' && !datos.web_habilitadas) {
    avisosUmbral.push(`Las sugerencias de la web empiezan cuando haya ${umbral} días con visitas guardadas (llevamos ${datos.web_dias_con_datos || 0}).`);
  }

  return (
    <Tarjeta titulo="Sugerencias de cambio" testid="uso-sugerencias">
      <AvisoInline aviso={aviso} onCerrar={limpiarAviso} className="mb-3" testId="uso-sugerencias-aviso" />
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
      {avisosUmbral.map((texto) => (
        <p key={texto} className="text-sm text-gray-500 mb-1" data-testid="uso-sugerencias-umbral">{texto}</p>
      ))}
      <div className="flex flex-wrap items-center gap-1 mb-2 mt-2" role="group" aria-label="Ámbito de las sugerencias">
        <span className="text-xs text-gray-500 mr-1">Ámbito</span>
        {AMBITOS_SUGERENCIAS.map((a) => {
          const total = a.id === 'todas' ? (conteoAmbito.erp || 0) + (conteoAmbito.web || 0) : conteoAmbito[a.id] || 0;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setAmbito(a.id)}
              className={`px-2.5 py-1 text-xs rounded-full border ${ambito === a.id ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              aria-pressed={ambito === a.id}
              data-uso="uso_erp.sugerencias_ambito"
              data-uso-detalle={a.id}
            >
              {a.label} ({total})
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {PESTANAS_SUGERENCIAS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPestana(p.id)}
            className={`px-3 py-1.5 text-sm rounded-lg border ${pestana === p.id ? 'bg-duetto-primary text-white border-duetto-primary' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            data-uso="uso_erp.sugerencias_pestana"
            data-uso-detalle={p.id}
          >
            {p.label} ({conteo[p.id] || 0})
          </button>
        ))}
      </div>
      {!datos && !error && <p className="text-sm text-gray-500">Cargando...</p>}
      {datos && visibles.length === 0 && <Vacio texto="No hay sugerencias en esta pestaña" />}
      <ul className="space-y-3">
        {visibles.map((s) => (
          <li key={s.id} className="border border-gray-200 rounded-lg p-3" data-testid="uso-sugerencia">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900 min-w-0">{s.titulo}</p>
              <div className="flex flex-wrap items-center gap-1 shrink-0">
                <span
                  className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${(INSIGNIA_AMBITO[s.ambito] || INSIGNIA_AMBITO.erp).clase}`}
                  data-testid="uso-sugerencia-ambito"
                >
                  {(INSIGNIA_AMBITO[s.ambito] || INSIGNIA_AMBITO.erp).texto}
                </span>
                <span className={`px-1.5 py-0.5 rounded text-[11px] ${NIVEL_CLASE[s.impacto] || NIVEL_CLASE.medio}`}>Impacto {s.impacto || 'medio'}</span>
                <span className={`px-1.5 py-0.5 rounded text-[11px] ${NIVEL_CLASE[s.esfuerzo === 'bajo' ? 'alto' : s.esfuerzo === 'alto' ? 'bajo' : 'medio']}`}>Esfuerzo {s.esfuerzo || 'medio'}</span>
              </div>
            </div>
            {s.problema_observado && (
              <p className="text-sm text-gray-700 mt-1.5"><span className="text-gray-500">Problema: </span>{s.problema_observado}</p>
            )}
            <p className="text-sm text-gray-800 mt-1"><span className="text-gray-500">Cambio propuesto: </span>{s.cambio_propuesto}</p>
            {s.evidencia && <p className="text-xs text-gray-500 mt-1 break-words">Evidencia: {s.evidencia}</p>}
            <p className="text-xs text-gray-400 mt-1">
              Propuesta el {fmtFechaLarga(s.fecha_dia)}
              {s.veces_reforzada > 0 && ` · reforzada ${s.veces_reforzada} ${s.veces_reforzada === 1 ? 'vez' : 'veces'}`}
            </p>
            {s.nota_direccion && <p className="text-xs text-gray-600 mt-1">Nota: {s.nota_direccion}</p>}
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(ACCIONES_SUGERENCIA).filter(([estado]) => estado !== s.estado).map(([estado, a]) => (
                <button
                  key={estado}
                  type="button"
                  onClick={() => abrir(s, estado)}
                  className={`px-3 py-1.5 text-xs rounded-lg border ${estado === 'descartada' ? 'border-gray-300 text-gray-600 hover:bg-gray-50' : 'border-duetto-primary text-duetto-primary hover:bg-emerald-50'}`}
                  data-uso={`uso_erp.sugerencia_${estado}`}
                >
                  {a.boton}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>
      <DialogoConfirmar
        abierto={!!dialogo}
        titulo={accion?.titulo || ''}
        descripcion={dialogo?.sugerencia?.titulo}
        textoConfirmar={accion?.confirmar || 'Confirmar'}
        peligro={dialogo?.estado === 'descartada'}
        ocupado={guardando}
        error={errorDialogo}
        onConfirmar={confirmar}
        onCancelar={cerrar}
        testId="uso-sugerencia-dialogo"
      >
        <label className="block text-sm text-gray-700">
          Nota (opcional)
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            maxLength={500}
            rows={3}
            className="block w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Por qué se acepta, se descarta o cómo se hizo"
          />
        </label>
      </DialogoConfirmar>
    </Tarjeta>
  );
};

// ─── Página ─────────────────────────────────────────────────────────────

const UsoErp = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const usuarioId = searchParams.get('usuario_id') || '';
  const [rango, setRango] = useState('30');
  const hoy = hoyMadrid();
  const [desdePers, setDesdePers] = useState(restarDias(hoy, 29));
  const [hastaPers, setHastaPers] = useState(hoy);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const params = useMemo(() => {
    const p = {};
    if (rango === 'personalizado') {
      if (desdePers) p.desde = desdePers;
      if (hastaPers) p.hasta = hastaPers;
    } else {
      p.hasta = hoy;
      p.desde = restarDias(hoy, Number(rango) - 1);
    }
    if (usuarioId) p.usuario_id = usuarioId;
    return p;
  }, [rango, desdePers, hastaPers, hoy, usuarioId]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const res = await usoErpAPI.resumen(params);
      setDatos(res.data);
    } catch (e) {
      const detalle = e?.response?.data?.detail;
      setError(typeof detalle === 'string' ? detalle : 'No se pudo cargar el uso del ERP');
    } finally {
      setCargando(false);
    }
  }, [params]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const elegirPersona = (id) => {
    const nuevos = new URLSearchParams(searchParams);
    if (id) nuevos.set('usuario_id', id);
    else nuevos.delete('usuario_id');
    setSearchParams(nuevos);
  };

  const usuarios = datos?.usuarios || [];
  const persona = usuarios.find((u) => u.usuario_id === usuarioId);
  const detalle = datos?.detalle;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="uso-erp-page">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-gradient-to-br from-duetto-primary to-duetto-secondary rounded-xl">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Uso del ERP</h1>
            <p className="text-sm text-gray-500">Pantallas, acciones y señales de problema de las personas medidas</p>
          </div>
        </div>

        <LecturaNoche />
        <SugerenciasCambio />

        {/* Filtros */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-col md:flex-row md:items-end gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-1">Periodo</p>
            <div className="flex flex-wrap gap-1">
              {RANGOS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRango(r.id)}
                  className={`px-3 py-1.5 text-sm rounded-lg border ${rango === r.id ? 'bg-duetto-primary text-white border-duetto-primary' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  data-uso="uso_erp.rango"
                  data-uso-detalle={r.id}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {rango === 'personalizado' && (
            <div className="flex gap-2">
              <label className="text-xs text-gray-500">
                Desde
                <input type="date" value={desdePers} max={hastaPers || undefined} onChange={(e) => setDesdePers(e.target.value)} className="block mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs text-gray-500">
                Hasta
                <input type="date" value={hastaPers} min={desdePers || undefined} onChange={(e) => setHastaPers(e.target.value)} className="block mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
              </label>
            </div>
          )}
          <label className="text-xs text-gray-500 md:ml-auto">
            Persona
            <select
              value={usuarioId}
              onChange={(e) => elegirPersona(e.target.value)}
              className="block mt-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm min-w-[200px] w-full md:w-auto"
              data-testid="uso-selector-persona"
            >
              <option value="">Todas (vista general)</option>
              {usuarios.map((u) => (
                <option key={u.usuario_id} value={u.usuario_id}>{u.nombre || u.usuario_id}</option>
              ))}
              {usuarioId && !persona && <option value={usuarioId}>Persona sin datos en el periodo</option>}
            </select>
          </label>
          <button
            type="button"
            onClick={cargar}
            disabled={cargando}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-1 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${cargando ? 'animate-spin' : ''}`} /> Actualizar
          </button>
        </div>

        {error && <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-2.5">{error}</div>}
        {datos?.truncado && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-2.5 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" /> Hay más de 50.000 eventos en el periodo: el resumen solo tiene en cuenta una parte. Acorta el rango.
          </div>
        )}
        {cargando && !datos && <p className="text-sm text-gray-500">Cargando...</p>}

        {datos && (
          <>
            {/* Señales: las de la persona elegida o las de todos */}
            <SenalesProblema senales={usuarioId ? detalle?.senales : datos.senales} />

            {usuarioId && datos.usuario_no_medido && (
              <div className="rounded-lg border border-gray-200 bg-white text-gray-600 text-sm px-4 py-3">
                Esta persona no está en la lista de personas medidas, así que no hay datos de su uso.
              </div>
            )}

            {/* Tabla general */}
            <Tarjeta titulo="Personas" testid="uso-tabla-general">
              {usuarios.length === 0 ? <Vacio texto="Todavía no hay uso registrado en el periodo" /> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[620px]">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b">
                        <th className="py-1.5 pr-2 font-medium">Persona</th>
                        <th className="py-1.5 pr-2 font-medium text-right">Eventos</th>
                        <th className="py-1.5 pr-2 font-medium text-right">Sesiones</th>
                        <th className="py-1.5 pr-2 font-medium text-right">Días activos</th>
                        <th className="py-1.5 pr-2 font-medium text-right">Tiempo</th>
                        <th className="py-1.5 pr-2 font-medium text-right">% móvil</th>
                        <th className="py-1.5 font-medium">Último uso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuarios.map((u) => (
                        <tr
                          key={u.usuario_id}
                          onClick={() => elegirPersona(u.usuario_id)}
                          className={`border-b border-gray-50 cursor-pointer hover:bg-gray-50 ${u.usuario_id === usuarioId ? 'bg-emerald-50' : ''}`}
                        >
                          <td className="py-1.5 pr-2">
                            <span className="font-medium text-gray-900">{u.nombre || u.usuario_id}</span>
                            {u.rol && <span className="text-xs text-gray-400"> · {u.rol}</span>}
                          </td>
                          <td className="py-1.5 pr-2 text-right">{u.eventos}</td>
                          <td className="py-1.5 pr-2 text-right">{u.sesiones}</td>
                          <td className="py-1.5 pr-2 text-right">{u.dias_activos}</td>
                          <td className="py-1.5 pr-2 text-right whitespace-nowrap">{fmtMinutos(u.minutos_estimados)}</td>
                          <td className="py-1.5 pr-2 text-right">{u.pct_movil} %</td>
                          <td className="py-1.5 whitespace-nowrap">{fmtFechaHora(u.ultimo_evento)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Tarjeta>

            {usuarioId && detalle && (
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-gray-900">{persona?.nombre || 'Detalle de la persona'}</h2>
                <DetallePersona detalle={detalle} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UsoErp;
